import { useState, useEffect, useRef, useReducer, useMemo } from 'react';
import { fetchExamQuestions, completeExam } from '../../api/fetchExam';
import { useAudioRecorderGeneral } from '../../hooks/useAudioRecorderGeneral';
import { useAppConfig } from '../../context/AppConfigContext';
import { useAssistant } from '../../components/Assistant/AssistantContext'; 
import NewUserHint from '../../components/NewUserHint/NewUserHint';
import Modal from '../../components/Modal';
import { toggleTranslation } from '../../utils/dialogUtils';
import styles from './ExamScreen.module.css';
import common from '../Lessons/Common.module.css';
import ExamResult from './ExamResult';
import useJokeDuringResult  from './getJokesByLanguage';
import { Capacitor } from '@capacitor/core';
import waitGif from '../wait100.gif';


import AudioTimeEnded from '../../components/AudioTimeEnded/AudioTimeEnded';

const examReducer = (state, action) => {
  switch (action.type) {
    case 'SET_RECOGNIZED_TEXT':
      return { ...state, recognizedText: action.payload };
    case 'SET_AUDIO_BLOB':
      return { ...state, audioBlob: action.payload };
    case 'SET_RESULT':
      return { ...state, result: action.payload };
    case 'SET_ASSISTANT_SUCCESS':
      return { ...state, success: action.payload };
    case 'TOGGLE_TRANSLATION':
      return { ...state, showTranslation: { ...state.showTranslation, [action.payload]: !state.showTranslation[action.payload] } };	  
    case 'RESET':
      return { ...state, recognizedText: '', audioBlob: null, success: false, textCleared: false };
	case 'SET_AUDIO_TIME_ENDED':
	  return { ...state, audioTimeEnded: action.payload }; 	 	
	case 'RESET_AUDIO_TIME_ENDED':
	  return { ...state, audioTimeEnded: false };	  
    default:
      return state;
  }
};

const ExamScreen = ({ userId, examId, onComplete, onExit, onGoPremium = null }) => {
  const { isTelegramWebApp, t, targetLanguage, isAndroidApp, site } = useAppConfig();
  const { actions, state: assistantState } = useAssistant();
  const [questions, setQuestions] = useState([]);
  const [introData, setIntroData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [questionVisible, setQuestionVisible] = useState(false);
  const buttonRef = useRef(null);
  const hasAssistantStartedSpeaking = useRef(false);
  const [hasPlayedIntro, setHasPlayedIntro] = useState(false);
  const [limitReached, setLimitReached] = useState(null);
  const isNative = Capacitor.isNativePlatform();
  const combinedIsAndroidApp = isAndroidApp || isNative; 
  const [newUserHintClosed, setNewUserHintClosed] = useState(false); 
  
  const [state, dispatch] = useReducer(examReducer, {
    recognizedText: '',
    audioBlob: null,
    result: null,
    success: false,
	textCleared: false, 
	showTranslation: {},
	audioTimeEnded: false,
  });
  
  const { audioTimeEnded } = state; 

  // Мемоизируем currentQuestion
  const currentQuestion = useMemo(() => {
    return questions[currentIndex]
      ? {
          id: questions[currentIndex].step,
          text: questions[currentIndex].question,
          translation: questions[currentIndex].translation,
          audio: questions[currentIndex].audio || null,
        }
      : null;
  }, [questions, currentIndex]);

  const stepId = currentQuestion?.id || currentIndex;
  const contextId = `exam-${examId}`;
   
  // 🎙️ Инициализация кастомного хука записи с автоотправкой на сервер
  const recorder = useAudioRecorderGeneral({
    userId,
    stepId,
    contextId,
    source: 'exam',
    dispatch,
    step: { 
		id: stepId, 
		examId 
		},
    state,
    buttonRef,
    attemptCount: 0,
    isTelegramWebApp,
    completeBlock: false, //currentIndex + 1 >= 5
  });

  // 📦 Получение экзаменационных вопросов при загрузке компонента
	useEffect(() => {
	  const fetchQuestions = async () => {
		try {
		  await fetchExamQuestions({ userId, examId, setData: setQuestions, setIntro: setIntroData, setLoading: setIsLoading, setError, setLimitReached });
		} catch (e) {
		  setError(e);
		}
	  };
	  if (userId && examId) {
		fetchQuestions();
	  }
	}, [userId, examId]);

	// ======= Управление видимостью вопроса и ассистентом! ============
	
	// Первый useEffect для обработки интро
	useEffect(() => {
	  if (!newUserHintClosed) return; // ⛔ ждём пока подсказка закроется		
	  if (currentIndex === 0 && !hasPlayedIntro && introData?.text) {

		const playIntroWithPause = async () => {
		  	  
		  await actions.speakText(introData.text, {
			variant: 'sticky',
			audioSrc: introData.audio ? `${combinedIsAndroidApp ? site : ''}${introData.audio}` : null,
			audioTimeoutMs: 20000,
		  });
		  await new Promise(resolve => setTimeout(resolve, 3000));
		  actions.resetState();
		  setHasPlayedIntro(true);
		};

		playIntroWithPause();
	  }
	}, [currentIndex, introData, newUserHintClosed]);

	// Второй useEffect для обработки вопросов, зависит от hasPlayedIntro
	useEffect(() => {
	  if (currentIndex === 0 && !hasPlayedIntro) return;
	  if (!currentQuestion?.text || !currentQuestion?.audio) return;

	  if (!hasAssistantStartedSpeaking.current) {
		const speakAfterPause = async () => {
		  if (currentIndex > 0) {
			actions.showSuccess();
			await new Promise(resolve => setTimeout(resolve, 2000));
		  }
		  await actions.speakText(currentQuestion.text, {
			audioSrc: currentQuestion.audio ? `${combinedIsAndroidApp ? site : ''}${currentQuestion.audio}` : null,
		  });
		  actions.resetState();
		  hasAssistantStartedSpeaking.current = true;
		};

		speakAfterPause();
	  }
	}, [currentQuestion?.text, hasPlayedIntro, currentIndex]);

	// ассистент задаёт вопросы
	useEffect(() => {
	  if (hasAssistantStartedSpeaking.current && !assistantState.bubbleText) {
		const timer = setTimeout(() => {
		  setQuestionVisible(true);
		  hasAssistantStartedSpeaking.current = false; // сбросить для следующего вопроса
		}, 1000);
		return () => clearTimeout(timer);
	  } 
	}, [assistantState.bubbleText]);

  // Автоматический переход к следующему вопросу при успехе
  useEffect(() => {
    if (state.success && currentIndex + 1 < questions.length) {
      const timer = setTimeout(() => {
        handleNext();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [state.success, currentIndex, questions.length]);

  // Переходим к следующему вопросу	
  const handleNext = () => {
    dispatch({ type: 'RESET' });
    dispatch({ type: 'SET_ASSISTANT_SUCCESS', payload: false });
    setStep(prev => prev + 1);
    setCurrentIndex(prev => prev + 1);
    setQuestionVisible(false);
  };
  
  //const isFinalQuestion = currentIndex + 1 >= 5; // или currentIndex + 1 >= questions.length
  const isWaitingResult = state.success && currentIndex + 1 >= questions.length && !state.result;

 
  useJokeDuringResult({ state, currentIndex, questions, actions, userId, examId, dispatch, targetLanguage });
   
  if (audioTimeEnded) { return <AudioTimeEnded onBack={() => { 
		dispatch({ type: 'RESET_AUDIO_TIME_ENDED' });
		onComplete();
	}} onGoPremium={onGoPremium} />; }    
  
  
  if (loading) { return <p>Loading...</p>; }
  if (error) return <p>Error: {error.message}</p>;
  if (!limitReached && !questions.length) return <p>No questions available</p>;
  if (state.result) { return ( <ExamResult result={state.result} onComplete={() => onExit(state.result.score, 5)} /> ); }
	
  //console.log("limitReached", limitReached);		
	return (
	  
	  <div className={styles.examScreen}>
	  
		{limitReached && (
		  <Modal
			title={t.examBlock.limitTitle}
			children={
			  <>
				{t.examBlock.limitText
				  .replace('{examsToday}', limitReached.examsToday)
				  .replace('{examLimit}', limitReached.examLimit)}
				{limitReached.vip === 0 && (
				  <div className="tempNote">
					{t.examBlock.limitUpgradeNote}
				  </div>
				)}
			  </>
			}
			onClose={onComplete}
			onConfirm={onComplete}
			confirmText={t.examBlock.limitButton}
			hideCancel={true}
		  />
		)}

		{!newUserHintClosed && (<NewUserHint source='exam' onClose={() => setNewUserHintClosed(true)} />)}
		
		{isWaitingResult ? (
		  <div className={styles.waitContainer}>
			<img src={waitGif} alt="Wait..." />
		  </div>
		) : (
		  <div className={`${styles.examMain} ${!questionVisible ? styles.hidden : ''}`}>
			<h1 className={styles.examTitle}>
			  {t.examBlock.title} ({currentIndex + 1}/{questions.length})
			</h1>

			{currentQuestion && questionVisible && (
			  <div className={styles.introBlock}>
				<div
				  className={styles.introTextContainer}
				  onClick={() => toggleTranslation(dispatch, `question-${step.id}`)}
				>
				  <p>{currentQuestion.text}</p>
				  {state.showTranslation[`question-${step.id}`] &&
					currentQuestion.translation && (
					  <p className={styles.translation}>{currentQuestion.translation}</p>
					)}
				</div>
			  </div>
			)}

			{!state.audioBlob && (
			  <div className={styles.recorderContainer}>
				<div className={styles.assistantLine}>
				  <button
					ref={buttonRef}
					type="button"
					className={`${styles.recordButton} ${
					  recorder.isRecording ? styles.recording : ''
					} ${!questionVisible ? styles.hidden : ''}`}
					disabled={
					  !recorder.permissionGranted ||
					  recorder.isProcessing ||
					  !questionVisible
					}
					onMouseDown={(e) => {
					  e.preventDefault();
					  recorder.handleStartRecording(currentQuestion?.options || []);
					}}
					onTouchStart={(e) => {
					  e.preventDefault();
					  recorder.handleStartRecording(currentQuestion?.options || []);
					}}
					onMouseUp={(e) => {
					  e.preventDefault();
					  recorder.handleStopRecording();
					}}
					onTouchEnd={(e) => {
					  e.preventDefault();
					  recorder.handleStopRecording();
					}}
				  >
					<svg viewBox="0 0 24 24">
					  <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zM11 21h2v-2h-2v2z" />
					</svg>
				  </button>
				</div>
			  </div>
			)}
		  </div>
		)}

	  </div>
	); 
};

export default ExamScreen;
